var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import clsx from 'clsx';
import { HawkinsPropsOverrideContext, isAssumedEventHandlerName, isNil, useClasses, useLayoutProps, useProps, useTranslate, } from '@hawkins/helpers';
import { USE_OVERFLOW_MENU_OVERFLOWABLE_ELEM_ATTR, useCombineRefs, useNavigationContext, useOverflowMenu, } from '../hooks';
import { Text } from '../text';
import { NavigationMenuGroupStrings } from './navigationMenuGroup.strings';
import { useNavigationMenuGroupStyles } from './navigationMenuGroup.styles';
import { NavigationMenuItem } from './navigationMenuItem';
import { NavigationMenuItemLevelContext } from './navigationMenuItem.context';
export var NAVIGATION_MENU_GROUP_OVERFLOWABLE_ELEM_ATTR = USE_OVERFLOW_MENU_OVERFLOWABLE_ELEM_ATTR;
var displayName = 'NavigationMenuGroup';
var actionOverrideProps = { Button: { compact: true } };
var getMenuItemProps = function (item, _a) {
    var _b;
    var idx = _a.idx;
    // If the item has a `trigger` prop, assume it is a `Menu`.
    var menuTrigger = item.props.trigger;
    // If the item is a `Menu`, assume that its trigger is a `NavigationMenuItem`.
    var assumedNavigationMenuItemProps = (menuTrigger || item).props;
    var itemChildren = (_b = assumedNavigationMenuItemProps.label) !== null && _b !== void 0 ? _b : assumedNavigationMenuItemProps.children;
    return __assign(__assign({}, Object.entries(assumedNavigationMenuItemProps).reduce(function (result, _a) {
        var _b = __read(_a, 2), propName = _b[0], propValue = _b[1];
        // `MenuItem` and `NavigationMenuItem` don't support all the same props, but at least
        // try to pass through event handlers they might have in common like `onClick` and `onFocus`.
        if (isAssumedEventHandlerName(propName))
            result[propName] = propValue;
        return result;
    }, {})), { children: itemChildren, component: assumedNavigationMenuItemProps.component, disabled: assumedNavigationMenuItemProps.disabled, href: assumedNavigationMenuItemProps.href, icon: assumedNavigationMenuItemProps.icon, key: "".concat(itemChildren, "_").concat(idx), nestedMenuContent: menuTrigger && item.props.children, to: assumedNavigationMenuItemProps.to });
};
/**
 * A `NavigationMenuGroup` is a grouping of menu items within a navigation component.
 * `NavigationMenuGroup` automatically styles itself based on the current navigation context
 * (horizontal versus vertical, collapsed versus expanded versus tiered, etc.).
 *
 * It is recommended that each child of this component be a `NavigationMenuItem`.
 *
 * If you include non-`NavigationMenuItem` elements as children, note that those elements
 * will NOT be rendered in this component’s overflow menu by default unless you set
 * the data attribute defined by `NAVIGATION_MENU_GROUP_OVERFLOWABLE_ELEM_ATTR` on the child’s root HTML element.
 */
export var NavigationMenuGroup = React.forwardRef(function (_a, ref) {
    var _b, _c, _d, _e;
    var children = _a.children, props = __rest(_a, ["children"]);
    // Props
    var _f = useProps(displayName, props), action = _f.action, classesProp = _f.classes, className = _f.className, collapsedLabel = _f.collapsedLabel, _g = _f.component, Component = _g === void 0 ? 'div' : _g, _h = _f.containerComponent, ContainerComponent = _h === void 0 ? 'ul' : _h, _j = _f.containerProps, _k = _j === void 0 ? {} : _j, _l = _k.ref, containerPropsRef = _l === void 0 ? null : _l, restContainerProps = __rest(_k, ["ref"]), _m = _f.disableOverflowableElemsWarning, disableOverflowableElemsWarning = _m === void 0 ? false : _m, _o = _f.disableOverflowMenu, disableOverflowMenu = _o === void 0 ? false : _o, label = _f.label, _p = _f.labelNoWrap, labelNoWrap = _p === void 0 ? false : _p, layout = _f.layout, maxDisplayElems = _f.maxDisplayElems, _q = _f.noTextTransform, noTextTransform = _q === void 0 ? false : _q, orientationProp = _f.orientation, styleProp = _f.style, translateProp = _f.translate, restProps = __rest(_f, ["action", "classes", "className", "collapsedLabel", "component", "containerComponent", "containerProps", "disableOverflowableElemsWarning", "disableOverflowMenu", "label", "labelNoWrap", "layout", "maxDisplayElems", "noTextTransform", "orientation", "style", "translate"]);
    // Context
    var _r = useNavigationContext(), isCollapsedArea = _r.isCollapsedArea, contextOrientation = _r.orientation;
    var level = React.useContext(NavigationMenuItemLevelContext);
    var orientation = orientationProp || contextOrientation;
    var isHorizontal = orientation === 'horizontal';
    var isNested = level > 0;
    var isOverflowMenuDisabled = !isHorizontal || disableOverflowMenu;
    var isVertical = orientation === 'vertical';
    // Strings
    var translate = useTranslate(translateProp, NavigationMenuGroupStrings);
    // Styles
    var baseClasses = useNavigationMenuGroupStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var style = useLayoutProps({ layout: layout, style: styleProp });
    var childrenContainerClassName = clsx(classes.childrenContainer, (_b = {},
        _b[classes.childrenContainerHorizontal] = isHorizontal,
        _b[classes.childrenContainerVertical] = isVertical,
        _b[classes.childrenContainerVerticalNested] = isVertical && isNested,
        _b));
    var labelContainerClassName = clsx(classes.labelContainer, (_c = {},
        _c[classes.labelContainerWithTextTransform] = !noTextTransform,
        _c));
    var labelRowClassName = clsx(classes.labelRow, (_d = {},
        _d[classes.labelRowVerticalNested] = isVertical && isNested,
        _d));
    var rootClassName = clsx(classes.HAWKINS_BASE, className, classes.root, (_e = {},
        _e[classes.rootHorizontalOverflowable] = isHorizontal && !isOverflowMenuDisabled,
        _e[classes.rootVertical] = isVertical,
        _e[classes.rootVerticalCollapsed] = isVertical && isCollapsedArea,
        _e));
    // Overflow menu logic
    var getMenuProps = React.useCallback(function (_a) {
        var _b;
        var isDone = _a.isDone, menuTriggerAttr = _a.menuTriggerAttr, overflowElemsRef = _a.overflowElemsRef;
        return ({
            trigger: (_jsx(NavigationMenuItem, __assign({}, (_b = {}, _b[menuTriggerAttr] = true, _b), { active: isDone &&
                    overflowElemsRef.current.some(function (elem) {
                        // If the element has a `trigger` prop, assume it is a `Menu`.
                        // If the element is a `Menu`, assume that its trigger is a `NavigationMenuItem`.
                        return (elem.props.trigger || elem).props.active;
                    }) }, { children: translate('navigationMenuGroup.overflowMenu.label') }))),
        });
    }, [translate]);
    var _s = useOverflowMenu({
        children: children,
        disabled: isOverflowMenuDisabled,
        disableOverflowableElemsWarning: disableOverflowableElemsWarning,
        displayName: displayName,
        getMenuItemProps: getMenuItemProps,
        getMenuProps: getMenuProps,
        maxDisplayElems: maxDisplayElems,
        overflowableElemAttr: NAVIGATION_MENU_GROUP_OVERFLOWABLE_ELEM_ATTR,
    }), containerRef = _s.containerRef, contentNode = _s.contentNode;
    var combinedContainerRef = useCombineRefs([containerRef, containerPropsRef]);
    // Content
    var childrenOverrideProps = React.useMemo(function () {
        var _a;
        return ({
            NavigationMenuItem: (_a = {},
                _a[NAVIGATION_MENU_GROUP_OVERFLOWABLE_ELEM_ATTR] = true,
                _a.labelNoWrap = isHorizontal && !isOverflowMenuDisabled,
                _a),
        });
    }, [isHorizontal, isOverflowMenuDisabled]);
    var actionNode = isCollapsedArea ? null : action;
    var derivedLabel = isCollapsedArea ? (collapsedLabel === null ? null : collapsedLabel !== null && collapsedLabel !== void 0 ? collapsedLabel : label) : label;
    return (_jsxs(Component, __assign({ className: rootClassName, ref: ref, style: style }, restProps, { children: [!isHorizontal && (!isNil(actionNode) || !isNil(derivedLabel)) && (_jsx(HawkinsPropsOverrideContext.Provider, __assign({ value: actionOverrideProps }, { children: _jsxs("div", __assign({ className: labelRowClassName }, { children: [!isNil(derivedLabel) && (_jsx("div", __assign({ className: labelContainerClassName }, { children: _jsx(Text, __assign({ bold: true, component: "div", className: classes.label, level: 0, noWrap: labelNoWrap, variant: "label" }, { children: derivedLabel })) }))), !isNil(actionNode) && _jsx("div", __assign({ className: classes.actionContainer }, { children: actionNode }))] })) }))), (!isNil(contentNode) ||
                // The `useResizeObserver` hook doesn't work if its ref is conditionally set,
                // so we must always render the overflowable container component for it to work.
                !isOverflowMenuDisabled) && (_jsx(HawkinsPropsOverrideContext.Provider, __assign({ value: childrenOverrideProps }, { children: _jsx(ContainerComponent, __assign({ className: childrenContainerClassName, ref: combinedContainerRef }, restContainerProps, { children: contentNode })) })))] })));
});
NavigationMenuGroup.displayName = displayName;
